import React, { useState } from 'react';
import axios from 'axios';
import { FaTimes, FaTicketAlt, FaPaperPlane } from 'react-icons/fa';

const SupportTicketModal = ({ isOpen, onClose, userInfo, API_BASE_URL, auth, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'other'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    show: false,
    type: '', // 'success' or 'error'
    title: '',
    message: ''
  });

  const priorityOptions = [
    { value: 'low', label: 'Low', color: '#95a5a6' },
    { value: 'medium', label: 'Medium', color: '#3498db' },
    { value: 'high', label: 'High', color: '#f39c12' },
    { value: 'urgent', label: 'Urgent', color: '#e74c3c' }
  ];

  const categoryOptions = [
    { value: 'technical_issue', label: 'Technical Issue' },
    { value: 'account_access', label: 'Account Access' },
    { value: 'data_correction', label: 'Data Correction' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'training_support', label: 'Training Support' },
    { value: 'system_error', label: 'System Error' },
    { value: 'payment_issue', label: 'Payment Issue' },
    { value: 'reporting_issue', label: 'Reporting Issue' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleNotificationClose = () => {
    setNotification({
      show: false,
      type: '',
      title: '',
      message: ''
    });

    // If it was a success notification, close the modal
    if (notification.type === 'success') {
      onClose();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const ticketData = {
        ...formData,
        created_by: userInfo.user_id
      };

      const response = await axios.post(
        `${API_BASE_URL}/support-tickets`,
        ticketData,
        auth
      );

      // Reset form
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        category: 'other'
      });

      // Show success notification
      setNotification({
        show: true,
        type: 'success',
        title: 'Support Ticket Created Successfully',
        message: response.data.message || `Your support ticket "${formData.title}" has been submitted successfully. You will receive updates on its progress.`
      });

      if (onSuccess) {
        onSuccess(response.data);
      }

      // Don't close immediately, let user see the success message
    } catch (error) {
      console.error('Error creating support ticket:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create support ticket';

      // Show error notification
      setNotification({
        show: true,
        type: 'error',
        title: 'Failed to Create Support Ticket',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    {/* Main Modal */}
    {isOpen && (
      <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        padding: '2rem',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <h2 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaTicketAlt color="#3498db" />
            Create Ticket to SuperAdmin
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#7f8c8d'
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Info */}
        <div style={{
          backgroundColor: '#e8f4fd',
          border: '1px solid #3498db',
          borderRadius: '0.25rem',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <p style={{ margin: 0, color: '#2c3e50', fontSize: '0.9rem' }}>
            <strong>Note:</strong> This ticket will be automatically sent to the SuperAdmin for review and assistance with MMDA-level issues.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              style={{
                width: '95%',
                padding: '0.75rem',
                border: `1px solid ${errors.title ? '#e74c3c' : '#ddd'}`,
                borderRadius: '0.25rem',
                fontSize: '1rem'
              }}
              placeholder="Brief description of the issue requiring SuperAdmin attention"
            />
            {errors.title && (
              <span style={{ color: '#e74c3c', fontSize: '0.8rem' }}>{errors.title}</span>
            )}
          </div>

          {/* Priority and Category Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            {/* Priority */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                {priorityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={6}
              style={{
                width: '95%',
                padding: '0.75rem',
                border: `1px solid ${errors.description ? '#e74c3c' : '#ddd'}`,
                borderRadius: '0.25rem',
                fontSize: '1rem',
                resize: 'vertical'
              }}
              placeholder="Please provide detailed information about the issue requiring SuperAdmin intervention. Include:
- What specific problem you're experiencing
- What you've already tried to resolve it
- How it's affecting your MMDA operations
- Any error messages or system behaviors
- Urgency and impact on field officers"
            />
            {errors.description && (
              <span style={{ color: '#e74c3c', fontSize: '0.8rem' }}>{errors.description}</span>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div style={{
              backgroundColor: '#fdf2f2',
              border: '1px solid #e74c3c',
              borderRadius: '0.25rem',
              padding: '0.75rem',
              marginBottom: '1rem',
              color: '#e74c3c'
            }}>
              {errors.submit}
            </div>
          )}

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: loading ? '#95a5a6' : '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FaPaperPlane />
              {loading ? 'Creating...' : 'Send to SuperAdmin'}
            </button>
          </div>
        </form>
      </div>
    </div>
    )}

    {/* Notification Modal - Rendered outside main modal */}
    {notification.show && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          padding: '2rem',
          maxWidth: '400px',
          width: '90%',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          textAlign: 'center'
        }}>
          {/* Icon */}
          <div style={{
            marginBottom: '1rem',
            fontSize: '3rem'
          }}>
            {notification.type === 'success' ? '✅' : '❌'}
          </div>

          {/* Title */}
          <h3 style={{
            margin: '0 0 1rem 0',
            color: notification.type === 'success' ? '#27ae60' : '#e74c3c',
            fontSize: '1.25rem',
            fontWeight: 'bold'
          }}>
            {notification.title}
          </h3>

          {/* Message */}
          <p style={{
            margin: '0 0 2rem 0',
            color: '#666',
            fontSize: '1rem',
            lineHeight: '1.5'
          }}>
            {notification.message}
          </p>

          {/* Close Button */}
          <button
            onClick={handleNotificationClose}
            style={{
              padding: '0.75rem 2rem',
              backgroundColor: notification.type === 'success' ? '#27ae60' : '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              minWidth: '100px'
            }}
          >
            OK
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default SupportTicketModal;
